import { Component, OnInit, ViewChild } from '@angular/core';
import { Router, ActivatedRoute, NavigationStart } from '@angular/router';
import { AuthService } from 'shared/auth/auth.service';
import { BrandService } from 'shared/brand/brand.service';
import { UserService } from 'shared/user/user.service';
import { PermissionsService } from 'shared/permissions/permissions.service';
import { SubscriptionService } from 'shared/subscription/subscription.service';
import { TermsService } from 'shared/terms/terms.service';
import { JwtHelperService } from '@auth0/angular-jwt';
import { pendoInitialize } from 'utils/pendo-analytics';
import { Config } from 'shared/config/env.config';
import { Observable, forkJoin } from 'rxjs';
import * as _ from 'lodash';

import { ProfilePasswordFormComponent } from 'shared/profile/profile-password-form/profile-password-form.component';

/**
 *    This class represents the lazy loaded LoginComponent.
 */

@Component({
    moduleId: module.id,
    selector: 'login-cmp',
    templateUrl: 'login.component.html',
    styleUrls: ['login.component.scss'],
    providers: [AuthService],
})

export class LoginComponent implements OnInit {
    loginInvalid: boolean = false;
    isCompleted: boolean = false;

    @ViewChild('profileForm', {static: false}) profileForm: ProfilePasswordFormComponent | void;

    constructor(
      private route: ActivatedRoute,
      private router: Router,
      private authService: AuthService,
      private brandService: BrandService,
      private userService: UserService,
      private permissionsService: PermissionsService,
      private subscriptionService: SubscriptionService,
      private termsService: TermsService,
      private jwtHelper: JwtHelperService,
    ) { }

    // getters
    get form() {
      return this.profileForm ? this.profileForm.form : null;
    }

    // hooks
    ngOnInit() {
      if (this.route.snapshot.url[0].path === 'logout') {
        this.userService.removeStoredUser();
        this.subscriptionService.clearStoredSubscription();
        this.termsService.clearStoredTerms();
        this.authService.logout();
      }
    }

    // handlers
    onLoginClick() {
      this.tryLogin();
    }

    onEnterClick() {
      this.tryLogin();
    }

    // private
    login(email: string, password: string) {
        this.loginInvalid = false;
        this.authService.authenticate(email, password)
            .subscribe(
                (response: any) => {
                    localStorage.setItem(Config.AUTH_TOKEN, response.token);
                    const d = this.jwtHelper.getTokenExpirationDate(response.token);

                    forkJoin([
                      this.brandService.getBrandList(),
                      this.userService.getUser()
                    ])
                    .subscribe((result: Array<any>) => {
                      const brands = result[0];
                      const user = result[1];

                      /*
                        USER
                        1. Store the user
                      */

                      this.userService.storeUser(user);

                      /*
                        BRANDS
                        1. get the auth brands
                        2. if there is a stored brand, make sure it is an auth brand.
                        3. if the stored brand is not in the list remove it and store the first one in the list
                      */
                      let storedBrand: any = this.brandService.getBrand();
                      // convert from string to number
                      if (storedBrand) { storedBrand = Number(storedBrand); }

                      if (!_.find(brands, ['id', storedBrand])) {
                        this.brandService.removeStoredBrand();
                        if (brands.length) {
                          this.brandService.changeSelectedBrand(brands[0]);
                        }
                      } else {
                        if (Config.PENDO_ANLYTICS_ID) {
                          pendoInitialize(user, this.brandService.getStoredBrand(), Config.PENDO_ANLYTICS_ID);
                        }

                      }


                      /*
                        PERMISSIONS
                        1. Clear stored permissions
                        2. Get the logged in User's permisssions
                      */
                      this.permissionsService.clearStoredPermissions();

                      this.permissionsService.getPermissions()
                        .subscribe((permissions) => {
                          this.router.navigate(['/']);
                        });

                    });
                },
                error => {
                    this.loginInvalid = true;
                    console.log(error);
                }
            );
    }

    private tryLogin() {
      if (!this.form) { return; }
      const value = this.form.value;
      this.login(value.email, value.currentPassword);
    }
}
